import { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { useParams } from 'react-router-dom';

import type { MetagameTokenPackageBundle } from '../../../dto/metagames.types';
import { useFlag } from '../../../services/launchDarkly';
import { platformContextService } from '../../../services/platformContext/PlatformContextService';
import type { PackageBundle } from '../../../stores/app/types';
import { isBingoCardsPackageBundles } from '../../../stores/bingo/helpers';
import { isCoinBackPackageBundles } from '../../../stores/coin-back/helpers';
import { isMetagameTokenPackageBundles } from '../../../stores/metagames/helpers';
import type { ChallengePayload, FreeSpinsPayload, MetaGameFeaturePayload } from '../../../stores/rewards/types';
import { SubscriptionUtils } from '../../../stores/subscription/utils';
import { useStores } from '../../../stores/useStores';
import { RouterProviderEnum } from '../../../types';
import { getIframeMaxHeight } from '../../../utils/payment-app.utils';

import type { PaymentServicePurchasePackage, PaymentsProps } from './types';

export function useIFrameParams(
  props: PaymentsProps,
  payComSessionData?: { sessionId: string; clientSessionSecretKey: string } | null,
) {
  const { loyaltyProgramStore, appStore, userStore, eventTriggerStore, subscriptionStore } = useStores();
  const { gameId } = useParams();
  const { currentOrderPackage, selectedPackage } = appStore;

  const paymentRouter = useFlag<RouterProviderEnum>('payment_router', RouterProviderEnum.PAY_COM);
  const payComApplePayRetry = useFlag<boolean>('pay_com_apple_pay_retry', false);
  const payComGooglePayRetry = useFlag<boolean>('pay_com_google_pay_retry', false);

  return useMemo(() => {
    if (isEmpty(selectedPackage)) {
      return undefined;
    }
    const { packageId, price, gcAmount, scAmount, packageBundles } = selectedPackage;

    const purchaseSource = sessionStorage.getItem('purchaseSource');
    const params = new URLSearchParams();
    const activeEventAllowedKeys = ['userEventTriggerId'] as const;
    const activeEventBenefitAllowedKeys = ['benefitType', 'key', 'offerId', 'offerType'] as const;
    const activeEventEventTriggerAllowedKeys = ['triggerKey', 'conditionsKey', 'eventTriggerId', 'department'] as const;

    const platform = platformContextService.getPaymentPlatform();
    const paymentEmitterPlatform = platformContextService.getPaymentEmitterPlatform();

    params.set('platform', platform);

    if (paymentEmitterPlatform) {
      params.set('paymentEmitterPlatform', paymentEmitterPlatform);
    }

    params.set('storeType', props.storeType);
    params.set('storeSegment', loyaltyProgramStore.currentTierKey);
    params.set('storeKey', appStore.currentStore?.key || '');

    params.set('packageId', `${packageId}`);
    params.set('amount', String(price));
    params.set('language', userStore.profile?.language || '');
    params.set('country', userStore.profile?.territory?.countryCode || '');
    params.set('currency', userStore.profile?.currency.code || '');
    params.set('paymentRouter', paymentRouter);
    params.set('ui_redesign_dialogs', 'true');
    params.set('pay_com_apple_pay_retry', String(payComApplePayRetry));
    params.set('pay_com_google_pay_retry', String(payComGooglePayRetry));

    // Deposit flow: the payments app opens on its own "Add cash" screen (skipping the
    // order summary) and mints the real package once the user confirms an amount.
    if (props.isDeposit) {
      params.set('packageType', 'deposit');
    }

    // Shipping flow: the payments app opens on the Crown Rips "Ship Card" screen with the
    // shipment context; the fee package is minted there once the quote is confirmed.
    if (props.shipping) {
      params.set('packageType', 'shipping');
      params.set('shipItemIds', props.shipping.providerItemIds.join(','));
      if (props.shipping.cardName) params.set('shipCardName', props.shipping.cardName);
      if (props.shipping.cardSubtitle) params.set('shipCardSubtitle', props.shipping.cardSubtitle);
      if (props.shipping.cardImageUrl) params.set('shipCardImage', props.shipping.cardImageUrl);
      if (props.shipping.sessionId) params.set('ripsSessionId', props.shipping.sessionId);
    }

    if (paymentRouter === RouterProviderEnum.PAY_COM && payComSessionData) {
      params.set('payComSessionId', payComSessionData.sessionId);
      params.set('payComClientSecret', payComSessionData.clientSessionSecretKey);
    }

    if (selectedPackage.subscriptionData) {
      const subscription = selectedPackage.subscriptionData;
      const quantity = subscription.quantity || 1;

      const currentSubscription = subscriptionStore.currentSubscription;
      const isUpgrade =
        currentSubscription?.status === 'active' &&
        currentSubscription.plan.id !== subscription.id &&
        subscription.type !== 'one-time';

      const packageInfo = {
        packageId: subscription.packageId,
        price: subscription.price * quantity,
        ccCards: SubscriptionUtils.getCardsCounts(subscription.benefitPackage.packageBundles, 'GC') * quantity,
        sweepsCards: SubscriptionUtils.getCardsCounts(subscription.benefitPackage.packageBundles, 'CASH') * quantity,
        ccCurrencySymbol: SubscriptionUtils.getCurrencySymbol(subscription.benefitPackage.packageBundles, 'GC'),
        sweepsCurrencySymbol: SubscriptionUtils.getCurrencySymbol(subscription.benefitPackage.packageBundles, 'CASH'),
        subscriptionType: subscription.type || 'subscription',
        billingCycle: subscription.cycleType,
        subscriptionName: subscription.name,
        quantity,
        ...(isUpgrade && {
          isUpgrade: true,
          payNowPrice: SubscriptionUtils.calculateUpgradeCharge(currentSubscription, subscription.price * quantity),
        }),
      };
      params.set('package', JSON.stringify(packageInfo));
      params.set('planId', subscription.id);
      params.set('packageType', 'subscription');
    } else {
      const isBingoCards =
        packageBundles && isMetagameTokenPackageBundles(packageBundles) && isBingoCardsPackageBundles(packageBundles);
      const metagamesTokenBundles = (packageBundles?.filter((bundle) => bundle.bundleType === 'metagameToken') ??
        []) as MetagameTokenPackageBundle[];
      const freeSpinsBundles =
        packageBundles?.filter(
          (bundle): bundle is PackageBundle & { bundlePayload: FreeSpinsPayload } =>
            bundle.bundleType === 'freeSpins' || bundle.bundleType === 'minigameRound',
        ) ?? [];
      const challengesBundles =
        packageBundles?.filter(
          (bundle): bundle is PackageBundle & { bundlePayload: ChallengePayload } => bundle.bundleType === 'challenge',
        ) ?? [];
      const metagameFeatureBundles =
        packageBundles?.filter(
          (bundle): bundle is PackageBundle & { bundlePayload: MetaGameFeaturePayload } =>
            bundle.bundleType === 'metagameFeature',
        ) ?? [];

      const purchasePackage: PaymentServicePurchasePackage = {
        packageId: Number(packageId),
        price: Number(price),
        gcCoin: Number(gcAmount),
        scCoin: Number(scAmount),
        ...(isBingoCards && {
          ccCards: SubscriptionUtils.getCardsCounts(packageBundles, 'GC'),
          sweepsCards: SubscriptionUtils.getCardsCounts(packageBundles, 'CASH'),
          ccCurrencySymbol: SubscriptionUtils.getCurrencySymbol(packageBundles, 'GC'),
          sweepsCurrencySymbol: SubscriptionUtils.getCurrencySymbol(packageBundles, 'CASH'),
        }),
        ...(packageBundles && isCoinBackPackageBundles(packageBundles)
          ? {
              coinBack: packageBundles.find((bundle) => bundle.bundleType === 'coinBack')?.bundlePayload,
            }
          : {}),
        ...(metagamesTokenBundles.length > 0
          ? {
              metagamesTokensBundles: metagamesTokenBundles.map((bundle) => ({
                id: bundle.id,
                amount: Number(bundle.bundlePayload.amount),
                currencySymbol: bundle.bundlePayload.currencySymbol,
                coinType: bundle.bundlePayload.coinType,
              })),
            }
          : {}),
        ...(freeSpinsBundles.length > 0
          ? {
              freeSpinsBundles: freeSpinsBundles.map((bundle) => ({
                id: bundle.id,
                gameImageUrl: bundle.bundlePayload.gameImageUrl,
                rounds: bundle.bundlePayload.rounds,
                coinType: bundle.bundlePayload.coinType,
                bet: bundle.bundlePayload.bet,
              })),
            }
          : {}),
        ...(challengesBundles.length > 0
          ? {
              challengesBundles: challengesBundles.map((bundle) => ({
                symbolUrl: bundle.bundlePayload.symbolUrl,
              })),
            }
          : {}),
        ...(metagameFeatureBundles.length > 0
          ? {
              metagameFeatureBundles: metagameFeatureBundles.map((bundle) => ({
                amount: bundle.bundlePayload.amount,
                symbolUrl: bundle.bundlePayload.symbolUrl,
                featureType: bundle.bundlePayload.featureType,
                metagameTypeName: bundle.bundlePayload.metagameTypeName,
              })),
            }
          : {}),
      };

      params.set('package', JSON.stringify(purchasePackage));
    }
    params.set('maxHeight', `${getIframeMaxHeight()}`);

    if (currentOrderPackage?.eventTriggerData && currentOrderPackage.eventTriggerData.activeEvent) {
      params.set(
        'eventTriggerData',
        JSON.stringify({
          activeEvent: {
            ...pick(currentOrderPackage.eventTriggerData.activeEvent, activeEventAllowedKeys),
            benefit: {
              ...pick(currentOrderPackage.eventTriggerData.activeEvent.benefit, activeEventBenefitAllowedKeys),
            },
            eventTrigger: {
              ...pick(
                currentOrderPackage.eventTriggerData.activeEvent.eventTrigger,
                activeEventEventTriggerAllowedKeys,
              ),
            },
          },
          // Forward the backend-evaluated flag (from the event-trigger response) so the
          // payment UI hides cash for offers while wsc is unavailable. Other methods unaffected.
          eventTriggerCashPurchaseEnabled:
            eventTriggerStore.lastEventTriggerResponse?.eventTriggerCashPurchaseEnabled ?? true,
        }),
      );

      params.set('packageType', 'offer');
    }

    if (currentOrderPackage?.eventTriggerData?.activeEvent?.benefit?.benefitType === 'road') {
      params.set('packageType', 'road');
    }

    if (gameId) {
      params.set('gameId', gameId);
    }

    if (purchaseSource) {
      params.set('purchaseSource', purchaseSource);
    }

    return params.toString();
  }, [
    currentOrderPackage,
    gameId,
    appStore.currentStore?.key,
    loyaltyProgramStore.currentTierKey,
    props.storeType,
    props.isDeposit,
    props.shipping,
    selectedPackage,
    subscriptionStore.currentSubscription,
    userStore.profile?.language,
    userStore.profile?.territory?.countryCode,
    userStore.profile?.currency.code,
    eventTriggerStore.lastEventTriggerResponse?.eventTriggerCashPurchaseEnabled,
    paymentRouter,
    payComApplePayRetry,
    payComGooglePayRetry,
    payComSessionData,
  ]);
}
